const template = document.createElement("template");

template.innerHTML = `
<style>
  :host{
    position: relative;
    display: inline-block;
    font-family: var(--ctl-font-sans, system-ui, -apple-system, Segoe UI, Roboto, Arial);
  }

  .tooltip{
    position: absolute;
    background: var(--ctl-color-surface-inverse);
    color: var(--ctl-color-on-surface-inverse);
    padding: var(--ctl-space-2) var(--ctl-space-2);
    border-radius: var(--ctl-radius-sm);
    box-shadow: var(--ctl-shadow-dropdown);
    font-size: var(--ctl-font-size-md);
    line-height: var(--ctl-leading-normal);
    max-width: var(--ctl-tooltip-max-width);
    white-space: normal;
    word-wrap: break-word;
    z-index: var(--ctl-z-tooltip);
    pointer-events: none;
    opacity: 0;
    transition: opacity var(--ctl-transition-normal);
  }

  .tooltip.visible{
    opacity: 1;
  }

  /* Positioning classes */
  .tooltip.top{
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%) translateY(calc(-1 * var(--ctl-space-2)));
  }

  .tooltip.bottom{
    top: 100%;
    left: 50%;
    transform: translateX(-50%) translateY(var(--ctl-space-2));
  }

  .tooltip.left{
    right: 100%;
    top: 50%;
    transform: translateY(-50%) translateX(calc(-1 * var(--ctl-space-2)));
  }

  .tooltip.right{
    left: 100%;
    top: 50%;
    transform: translateY(-50%) translateX(var(--ctl-space-2));
  }

  /* Arrow */
  .tooltip::before{
    content: "";
    position: absolute;
    width: 0;
    height: 0;
    border: var(--ctl-tooltip-arrow-size) solid transparent;
  }

  .tooltip.top::before{
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border-top-color: var(--ctl-color-surface-inverse);
  }

  .tooltip.bottom::before{
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    border-bottom-color: var(--ctl-color-surface-inverse);
  }

  .tooltip.left::before{
    left: 100%;
    top: 50%;
    transform: translateY(-50%);
    border-left-color: var(--ctl-color-surface-inverse);
  }

  .tooltip.right::before{
    right: 100%;
    top: 50%;
    transform: translateY(-50%);
    border-right-color: var(--ctl-color-surface-inverse);
  }

  .trigger{
    cursor: help;
  }

  /* Touch device adjustments */
  @media (hover: none) and (pointer: coarse) {
    .trigger {
      cursor: default;
      -webkit-tap-highlight-color: transparent;
    }
    
    .tooltip {
      /* Slightly larger on touch devices */
      padding: var(--ctl-space-2) var(--ctl-space-3);
      font-size: var(--ctl-font-size-base);
    }
  }
</style>

<span class="trigger" part="trigger" id="trigger">
  <slot></slot>
</span>

<div class="tooltip" part="tooltip" id="tooltip" role="tooltip">
  <slot name="tooltip"></slot>
</div>
`;

export class CtlTooltip extends HTMLElement {
  static get observedAttributes() {
    return ["text", "placement", "delay"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));

    this._trigger = this.shadowRoot.getElementById("trigger");
    this._tooltip = this.shadowRoot.getElementById("tooltip");
    this._tooltipSlot = this._tooltip.querySelector('slot[name="tooltip"]');
    this._tooltipTextNode = document.createTextNode("");
    this._showTimeout = null;
    this._hideTimeout = null;
    this._isTouchDevice = false;
    this._touchTimeout = null;
    
    // Generate unique ID for tooltip accessibility
    this._tooltipId = `tooltip-${Math.random().toString(36).substr(2, 9)}`;

    this._onMouseEnter = this._onMouseEnter.bind(this);
    this._onMouseLeave = this._onMouseLeave.bind(this);
    this._onFocus = this._onFocus.bind(this);
    this._onBlur = this._onBlur.bind(this);
    this._onTouchStart = this._onTouchStart.bind(this);
    this._onTouchEnd = this._onTouchEnd.bind(this);
  }

  connectedCallback() {
    // Set unique ID on tooltip for accessibility
    this._tooltip.id = this._tooltipId;
    
    // Get the trigger element from default slot
    const triggerSlot = this._trigger.querySelector('slot:not([name])');
    if (triggerSlot) {
      this._triggerSlot = triggerSlot;
      // Store handler for cleanup
      this._slotChangeHandler = () => {
        const assignedElements = triggerSlot.assignedElements();
        if (assignedElements.length > 0) {
          this._triggerElement = assignedElements[0];
          
          // Preserve existing aria-describedby values
          const existingDescribedBy = this._triggerElement.getAttribute('aria-describedby');
          if (existingDescribedBy) {
            // Save original value for restoration
            this._originalDescribedBy = existingDescribedBy;
            // Append our tooltip ID to existing values
            const ids = existingDescribedBy.split(/\s+/);
            if (!ids.includes(this._tooltipId)) {
              this._triggerElement.setAttribute('aria-describedby', `${existingDescribedBy} ${this._tooltipId}`);
            }
          } else {
            this._originalDescribedBy = null;
            this._triggerElement.setAttribute('aria-describedby', this._tooltipId);
          }
        }
      };
      
      triggerSlot.addEventListener('slotchange', this._slotChangeHandler);
      this._slotChangeHandler(); // Initial setup
    }
    
    this._syncPlacement();
    this._syncText();

    // Detect touch device
    this._isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    if (this._isTouchDevice) {
      // On touch devices, use tap to show/hide
      this._trigger.addEventListener("touchstart", this._onTouchStart, { passive: false });
      this._trigger.addEventListener("touchend", this._onTouchEnd, { passive: false });
    } else {
      // On hover devices, use mouse events
      this._trigger.addEventListener("mouseenter", this._onMouseEnter);
      this._trigger.addEventListener("mouseleave", this._onMouseLeave);
    }
    
    this._trigger.addEventListener("focus", this._onFocus, true);
    this._trigger.addEventListener("blur", this._onBlur, true);
  }

  disconnectedCallback() {
    // Restore original aria-describedby or remove our tooltip ID
    if (this._triggerElement) {
      if (this._originalDescribedBy) {
        this._triggerElement.setAttribute('aria-describedby', this._originalDescribedBy);
      } else {
        // Remove only our tooltip ID, preserve others
        const existingDescribedBy = this._triggerElement.getAttribute('aria-describedby');
        if (existingDescribedBy) {
          const ids = existingDescribedBy.split(/\s+/).filter(id => id !== this._tooltipId);
          if (ids.length > 0) {
            this._triggerElement.setAttribute('aria-describedby', ids.join(' '));
          } else {
            this._triggerElement.removeAttribute('aria-describedby');
          }
        }
      }
    }
    
    // Remove slotchange listener
    if (this._triggerSlot && this._slotChangeHandler) {
      this._triggerSlot.removeEventListener('slotchange', this._slotChangeHandler);
    }
    
    if (this._isTouchDevice) {
      this._trigger.removeEventListener("touchstart", this._onTouchStart);
      this._trigger.removeEventListener("touchend", this._onTouchEnd);
    } else {
      this._trigger.removeEventListener("mouseenter", this._onMouseEnter);
      this._trigger.removeEventListener("mouseleave", this._onMouseLeave);
    }
    this._trigger.removeEventListener("focus", this._onFocus, true);
    this._trigger.removeEventListener("blur", this._onBlur, true);
    this._clearTimeouts();
  }

  attributeChangedCallback(name) {
    if (name === "placement") this._syncPlacement();
    if (name === "text") this._syncText();
  }

  // --- Public API ---
  show() {
    this._clearTimeouts();
    const delay = parseInt(this.getAttribute("delay") || "0", 10);
    
    this._showTimeout = setTimeout(() => {
      this._tooltip.classList.add("visible");
      this.dispatchEvent(
        new CustomEvent("ctl-tooltip-show", { bubbles: true, composed: true })
      );
    }, delay);
  }

  hide() {
    this._clearTimeouts();
    this._tooltip.classList.remove("visible");
    this.dispatchEvent(
      new CustomEvent("ctl-tooltip-hide", { bubbles: true, composed: true })
    );
  }

  // --- Internal ---
  _clearTimeouts() {
    if (this._showTimeout) clearTimeout(this._showTimeout);
    if (this._hideTimeout) clearTimeout(this._hideTimeout);
    if (this._touchTimeout) clearTimeout(this._touchTimeout);
    this._showTimeout = null;
    this._hideTimeout = null;
    this._touchTimeout = null;
  }

  _syncPlacement() {
    const placement = this.getAttribute("placement") || "top";
    const validPlacements = ["top", "bottom", "left", "right"];
    
    // Remove all placement classes
    validPlacements.forEach((p) => this._tooltip.classList.remove(p));
    
    // Add current placement
    if (validPlacements.includes(placement)) {
      this._tooltip.classList.add(placement);
    } else {
      this._tooltip.classList.add("top");
    }
  }

  _syncText() {
    const text = this.getAttribute("text");
    
    // If the slot has assigned elements (user provided content), don't override
    const hasSlottedContent = this._tooltipSlot && this._tooltipSlot.assignedElements().length > 0;
    
    if (hasSlottedContent) {
      // User provided slotted content takes precedence
      return;
    }
    
    // Use text attribute as fallback content
    if (text) {
      this._tooltipTextNode.textContent = text;
      if (!this._tooltipTextNode.parentNode) {
        this._tooltip.appendChild(this._tooltipTextNode);
      }
    } else {
      // Remove text node when text attribute is removed
      if (this._tooltipTextNode.parentNode) {
        this._tooltip.removeChild(this._tooltipTextNode);
      }
    }
  }

  _onMouseEnter() {
    this.show();
  }

  _onMouseLeave() {
    this.hide();
  }

  _onFocus() {
    this.show();
  }

  _onBlur() {
    this.hide();
  }

  _onTouchStart(e) {
    // Check visibility once
    const isVisible = this._tooltip.classList.contains('visible');
    
    // Only prevent default if tooltip is already showing to allow scrolling
    if (isVisible) {
      e.preventDefault();
      this.hide();
    } else {
      this.show();
      
      // Auto-hide after 3 seconds on touch devices
      if (this._touchTimeout) clearTimeout(this._touchTimeout);
      this._touchTimeout = setTimeout(() => {
        this.hide();
      }, 3000);
    }
  }

  _onTouchEnd(e) {
    // Only prevent default if tooltip is showing
    if (this._tooltip.classList.contains('visible')) {
      e.preventDefault();
    }
  }
}

customElements.define("ctl-tooltip", CtlTooltip);
